import {
  NAKSHATRA_NAMES,
  SIGN_NAMES,
  type AyanamsaId,
  type DashaPossibility,
  type FeaturePossibility,
  type Planet,
  type StableFeature,
  type TimeConfidence,
  type UnavailableFeature,
  type UncertaintyReport,
  type VaryingFeature,
} from '@astrally/shared';
import type { EphemerisAdapter } from '../ephemeris/adapter.js';
import { getAyanamsa } from '../ayanamsa/lahiri.js';
import { norm360 } from '../math/angles.js';
import { computeAngles } from '../core/houses.js';
import { computePositions } from '../core/positions.js';
import { nakshatraOf, rashiName, signOf } from '../core/zodiac.js';
import { d10Sign, d9Sign } from '../jyotish/vargas.js';
import { activePeriods, computeVimshottari } from '../jyotish/vimshottari.js';
import { computeAspects, type ChartPoint } from '../western/aspects.js';

/**
 * Scans a candidate birth-time window and classifies chart features as
 * stable / varying / unavailable. This is what makes approximate and unknown
 * birth times first-class product modes instead of error states.
 */

export interface WindowScanInput {
  /** UTC instants of the window edges (converted from local by the caller). */
  utcStart: Date;
  utcEnd: Date;
  /** Local clock labels for presentation ("HH:MM"). */
  localStartLabel: string;
  localEndLabel: string;
  latitude: number;
  longitude: number;
  mode: Extract<TimeConfidence, 'approximate' | 'unknown'>;
  stepMinutes?: number;
  ayanamsaId?: AyanamsaId;
}

interface Sample {
  utc: Date;
  minutes: number;
  ascTropicalSign: number;
  lagnaSiderealSign: number;
  mcTropicalSign: number;
  moonLonSidereal: number;
  fastSigns: Map<string, string>; // featureId -> value label
}

const FAST_PLANETS: Planet[] = ['moon', 'sun', 'mercury', 'venus', 'mars'];
const SLOW_PLANETS: Planet[] = ['jupiter', 'saturn', 'uranus', 'neptune', 'pluto', 'rahu', 'ketu'];

function minutesLabel(startLabel: string, offsetMin: number): string {
  const [h, m] = startLabel.split(':').map(Number);
  const total = (h * 60 + m + offsetMin) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

export function scanWindow(input: WindowScanInput, adapter: EphemerisAdapter): UncertaintyReport {
  const step = input.stepMinutes ?? 10;
  const ayanamsaId = input.ayanamsaId ?? 'lahiri';
  const spanMs = input.utcEnd.getTime() - input.utcStart.getTime();
  if (spanMs <= 0) throw new Error('Window end must be after start');
  const stepMs = step * 60000;

  // --- collect samples ------------------------------------------------------
  const samples: Sample[] = [];
  for (let t = input.utcStart.getTime(); ; t += stepMs) {
    const clamped = Math.min(t, input.utcEnd.getTime());
    const utc = new Date(clamped);
    const ayan = getAyanamsa(ayanamsaId).valueDeg(adapter.julianCenturiesTT(utc));
    const angles = computeAngles(utc, input.latitude, input.longitude, adapter, ayan);
    const fastSigns = new Map<string, string>();
    let moonLonSidereal = 0;
    for (const planet of FAST_PLANETS) {
      const lon = adapter.position(planet, utc).lon;
      const sid = norm360(lon - ayan);
      if (planet === 'moon') moonLonSidereal = sid;
      fastSigns.set(`planet.${planet}.tropicalSign`, SIGN_NAMES[signOf(lon)]);
      fastSigns.set(`planet.${planet}.siderealSign`, rashiName(signOf(sid)));
      fastSigns.set(`planet.${planet}.nakshatra`, NAKSHATRA_NAMES[nakshatraOf(sid).index]);
      fastSigns.set(`planet.${planet}.d9`, rashiName(d9Sign(sid)));
      fastSigns.set(`planet.${planet}.d10`, rashiName(d10Sign(sid)));
    }
    // Birth-time-dependent dasha state
    const vim = computeVimshottari(moonLonSidereal, utc, 365.25, 1);
    fastSigns.set('dasha.birthMahadasha', vim.moonNakshatra.lord);
    const nowChain = activePeriods(vim, new Date());
    fastSigns.set('dasha.currentMahadasha', nowChain[0]?.lord ?? 'unknown');

    samples.push({
      utc,
      minutes: Math.round((clamped - input.utcStart.getTime()) / 60000),
      ascTropicalSign: signOf(angles.ascendant),
      lagnaSiderealSign: signOf(angles.ascendantSidereal),
      mcTropicalSign: signOf(angles.mc),
      moonLonSidereal,
      fastSigns,
    });
    if (clamped >= input.utcEnd.getTime()) break;
  }

  // Slow planets: endpoints + midpoint only.
  const midUtc = new Date(input.utcStart.getTime() + spanMs / 2);
  const slowValues = new Map<string, Set<string>>();
  for (const utc of [input.utcStart, midUtc, input.utcEnd]) {
    const ayan = getAyanamsa(ayanamsaId).valueDeg(adapter.julianCenturiesTT(utc));
    for (const planet of SLOW_PLANETS) {
      const lon = adapter.position(planet, utc).lon;
      const sid = norm360(lon - ayan);
      const entries: [string, string][] = [
        [`planet.${planet}.tropicalSign`, SIGN_NAMES[signOf(lon)]],
        [`planet.${planet}.siderealSign`, rashiName(signOf(sid))],
        [`planet.${planet}.nakshatra`, NAKSHATRA_NAMES[nakshatraOf(sid).index]],
        [`planet.${planet}.d9`, rashiName(d9Sign(sid))],
        [`planet.${planet}.d10`, rashiName(d10Sign(sid))],
      ];
      for (const [k, v] of entries) {
        const set = slowValues.get(k) ?? new Set<string>();
        set.add(v);
        slowValues.set(k, set);
      }
    }
  }

  // --- classify -------------------------------------------------------------
  const stable: StableFeature[] = [];
  const varying: VaryingFeature[] = [];
  const unavailable: UnavailableFeature[] = [];

  const featureLabel = (id: string): { label: string; system: 'jyotish' | 'western' | 'both' } => {
    const [, planet, kind] = id.split('.');
    const cap = planet.charAt(0).toUpperCase() + planet.slice(1);
    switch (kind) {
      case 'tropicalSign':
        return { label: `${cap} — tropical sign`, system: 'western' };
      case 'siderealSign':
        return { label: `${cap} — rashi (sidereal)`, system: 'jyotish' };
      case 'nakshatra':
        return { label: `${cap} — nakshatra`, system: 'jyotish' };
      case 'd9':
        return { label: `${cap} — Navamsa (D9) sign`, system: 'jyotish' };
      case 'd10':
        return { label: `${cap} — Dasamsa (D10) sign`, system: 'jyotish' };
      default:
        return { label: id, system: 'both' };
    }
  };

  const classifySeries = (
    id: string,
    label: string,
    system: 'jyotish' | 'western' | 'both',
    values: string[],
  ) => {
    const distinct = [...new Set(values)];
    if (distinct.length === 1) {
      stable.push({ id, label, value: distinct[0], system });
      return;
    }
    const possibilities: FeaturePossibility[] = [];
    let runStart = 0;
    for (let i = 1; i <= values.length; i++) {
      if (i === values.length || values[i] !== values[runStart]) {
        possibilities.push({
          value: values[runStart],
          fromLocal: minutesLabel(input.localStartLabel, samples[runStart].minutes),
          toLocal: minutesLabel(
            input.localStartLabel,
            samples[Math.min(i, values.length) - 1].minutes,
          ),
          fraction: Number(((i - runStart) / values.length).toFixed(3)),
        });
        runStart = i;
      }
    }
    varying.push({ id, label, system, possibilities });
  };

  // Fast-planet features
  const fastIds = [...samples[0].fastSigns.keys()].filter((k) => k.startsWith('planet.'));
  for (const id of fastIds) {
    const { label, system } = featureLabel(id);
    classifySeries(id, label, system, samples.map((s) => s.fastSigns.get(id) ?? ''));
  }

  // Slow-planet features
  for (const [id, set] of slowValues) {
    const { label, system } = featureLabel(id);
    if (set.size === 1) {
      stable.push({ id, label, value: [...set][0], system });
    } else {
      // Rare (sign boundary during the day): present possibilities without ranges.
      varying.push({
        id,
        label,
        system,
        possibilities: [...set].map((value) => ({
          value,
          fromLocal: input.localStartLabel,
          toLocal: input.localEndLabel,
          fraction: Number((1 / set.size).toFixed(3)),
        })),
      });
    }
  }

  // Angles: possibilities when approximate, hidden when unknown.
  if (input.mode === 'approximate') {
    classifySeries(
      'angles.ascendant.tropicalSign',
      'Ascendant (Western rising sign)',
      'western',
      samples.map((s) => SIGN_NAMES[s.ascTropicalSign]),
    );
    classifySeries(
      'angles.lagna.siderealSign',
      'Lagna (Jyotish rising sign)',
      'jyotish',
      samples.map((s) => rashiName(s.lagnaSiderealSign)),
    );
    classifySeries(
      'angles.mc.tropicalSign',
      'Midheaven sign',
      'western',
      samples.map((s) => SIGN_NAMES[s.mcTropicalSign]),
    );
    unavailable.push({
      id: 'houses.placidus',
      label: 'Exact house cusps',
      reason: 'House degrees move continuously with birth time; narrow the window to unlock.',
    });
  } else {
    unavailable.push(
      {
        id: 'angles.ascendant',
        label: 'Ascendant / rising sign',
        reason: 'Changes roughly every two hours; needs a birth time.',
      },
      {
        id: 'angles.lagna',
        label: 'Jyotish lagna',
        reason: 'Changes roughly every two hours; needs a birth time.',
      },
      {
        id: 'angles.mc',
        label: 'Midheaven',
        reason: 'Needs a birth time.',
      },
      {
        id: 'houses.all',
        label: 'Houses & bhava positions',
        reason: 'Depend on the rising degree; hidden rather than guessed.',
      },
      {
        id: 'vargas.lagna-dependent',
        label: 'Lagna-dependent varga positions',
        reason: 'Time-sensitive divisional placements need a birth time.',
      },
      {
        id: 'yogas.lagna-dependent',
        label: 'Lagna-based yogas',
        reason: 'Evaluated only with an exact birth time.',
      },
    );
  }

  // Aspect stability (western): computed at start / mid / end samples.
  const aspectSets = [input.utcStart, midUtc, input.utcEnd].map((utc) => {
    const pts: ChartPoint[] = computePositions(utc, adapter)
      .filter((p) => p.planet !== 'rahu' && p.planet !== 'ketu')
      .map((p) => ({ id: p.planet, lon: p.lonTropical }));
    return computeAspects(pts).map((a) => `${a.a}|${a.type}|${a.b}`);
  });
  const inAll = aspectSets[0].filter((k) => aspectSets.every((s) => s.includes(k)));
  const inSome = [...new Set(aspectSets.flat())].filter((k) => !inAll.includes(k));
  for (const key of inAll) {
    const [a, type, b] = key.split('|');
    stable.push({
      id: `western.aspect.${a}-${type}-${b}`,
      label: 'Aspect',
      value: `${a} ${type} ${b}`,
      system: 'western',
    });
  }
  for (const key of inSome) {
    const [a, type, b] = key.split('|');
    varying.push({
      id: `western.aspect.${a}-${type}-${b}`,
      label: 'Aspect (depends on exact time)',
      system: 'western',
      possibilities: [
        {
          value: `${a} ${type} ${b} — holds during part of the window`,
          fromLocal: input.localStartLabel,
          toLocal: input.localEndLabel,
          fraction: 0.5,
        },
      ],
    });
  }

  // Dasha possibilities at birth.
  const dashaCounts = new Map<string, { lord: Planet; nak: string; count: number }>();
  for (const s of samples) {
    const nak = nakshatraOf(s.moonLonSidereal);
    const key = `${nak.lord}|${nak.name}`;
    const cur = dashaCounts.get(key) ?? { lord: nak.lord, nak: nak.name, count: 0 };
    cur.count += 1;
    dashaCounts.set(key, cur);
  }
  const dashaPossibilities: DashaPossibility[] = [...dashaCounts.values()].map((d) => ({
    mahadashaLord: d.lord,
    nakshatraName: d.nak,
    fraction: Number((d.count / samples.length).toFixed(3)),
  }));

  const currentDashaValues = [...new Set(samples.map((s) => s.fastSigns.get('dasha.currentMahadasha')))];
  if (currentDashaValues.length === 1 && currentDashaValues[0] !== 'unknown') {
    stable.push({
      id: 'dasha.currentMahadasha',
      label: 'Current mahadasha (today)',
      value: String(currentDashaValues[0]),
      system: 'jyotish',
      detail: 'Holds across your entire birth-time window.',
    });
  } else if (currentDashaValues.length > 1) {
    classifySeries(
      'dasha.currentMahadasha',
      'Current mahadasha (today)',
      'jyotish',
      samples.map((s) => String(s.fastSigns.get('dasha.currentMahadasha'))),
    );
  }

  const moonLons = samples.map((s) => s.moonLonSidereal);

  return {
    windowLocalStart: input.localStartLabel,
    windowLocalEnd: input.localEndLabel,
    stepMinutes: step,
    sampleCount: samples.length,
    stable,
    varying,
    unavailable,
    midpointPositions: computePositions(midUtc, adapter),
    moonRangeSidereal: { start: moonLons[0], end: moonLons[moonLons.length - 1] },
    dasha: {
      possibilities: dashaPossibilities,
      note:
        dashaPossibilities.length > 1
          ? 'Your Moon changes nakshatra inside this window, so the dasha sequence has more than one possibility. We interpret each and explain what stays common.'
          : 'Dasha start dates still carry uncertainty proportional to the window width.',
    },
  };
}
